// Real backend integration for the read-only PRO tree (영역 → 하위영역 → 항목).
// Maps the server error envelope `{ error: { code, message } }` to a generic
// Error — the PRO tree has no per-feature error class because every consumer
// (PRD 04/05/06) only needs the success path.
// Activated by setting VITE_USE_MOCK_API=false; see pro-tree.adapter.ts.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import type { ProTreeNode } from '../types/pro-tree.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: { code?: string; message?: string };
}

// Wire shape returned by `GET /admin/pro/tree` (BE `ProTreeResponse`). The tree
// is nested domains → subDomains → items, NOT a flat `ProTreeNode[]`; this
// module is the single place that bridges that contract to the FE shape.
interface ProTreeItemDto {
  id: number;
  pid: string; // server-issued display code (P-XXXX) — the questions endpoint keys on this
  name: string;
  status: string;
  subDomainId: number;
  // SHCT-432 (Option B) — measurement schedule as codes; FE maps to labels.
  cycle?: string | null; // ProMeasurementCycle code
  daysOfWeek?: string[] | null; // DayOfWeek codes (MON..SUN)
  // SHCT-512 / SHCT-517 — [ADM.046]: the item's Scope. A question does not own one; it inherits
  // this. Nullable because an older server build predates the field.
  scope?: string | null; // COMMON | DISEASE_GROUP | DISEASE_SPECIFIC
  diseaseGroupId?: number | null;
  diseaseCodeId?: number | null;
}
interface ProTreeSubDomainDto {
  id: number;
  name: string;
  sortOrder: number;
  items: ProTreeItemDto[] | null;
}
interface ProTreeDomainDto {
  id: number;
  name: string;
  sortOrder: number;
  subDomains: ProTreeSubDomainDto[] | null;
}
interface ProTreeResponseDto {
  domains: ProTreeDomainDto[];
}

function rethrow(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new Error(payload?.message ?? 'PRO 트리 데이터를 불러올 수 없습니다.');
}

// Flatten the BE's nested record tree into the FE's recursive `ProTreeNode[]`.
// `code` is the positional display label (e.g. '1', '1-2', '1-2-1') the panel
// renders; item `id` is the server `pid` (P-XXXX) so a tree selection feeds
// `GET /items/{pid}/questions` and matches `question.itemId` for the badges.
function toProTree(body: ProTreeResponseDto): ProTreeNode[] {
  return body.domains.map((domain, di) => {
    const areaCode = String(di + 1);
    return {
      id: `area-${domain.id}`,
      type: 'area',
      code: areaCode,
      label: domain.name,
      children: (domain.subDomains ?? []).map((sub, si) => {
        const subCode = `${areaCode}-${si + 1}`;
        return {
          id: `sub-${sub.id}`,
          type: 'sub',
          code: subCode,
          label: sub.name,
          children: (sub.items ?? []).map((item, ii) => ({
            id: item.pid,
            type: 'item',
            code: `${subCode}-${ii + 1}`,
            label: item.name,
            cycle: item.cycle ?? undefined,
            days: item.daysOfWeek ?? undefined,
            numericId: item.id,
            // SHCT-512 / SHCT-517 — [ADM.046] item Scope, now carried by the tree itself.
            scope: item.scope ?? undefined,
            diseaseGroupId: item.diseaseGroupId ?? undefined,
            diseaseCodeId: item.diseaseCodeId ?? undefined,
          })),
        };
      }),
    };
  });
}

export interface ProTreeApi {
  listProTree(): Promise<ProTreeNode[]>;
}

export const proTreeHttpApi: ProTreeApi = {
  async listProTree(): Promise<ProTreeNode[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<ProTreeResponseDto>>(
        endpoints.proTree,
      );
      const domains = res.data?.data?.domains;
      // The backend contract is `{ data: { domains: [...] } }`. A missing/
      // non-array `domains` (wrong envelope, SPA fallback HTML, …) must surface
      // as a load error — not flow into the mapper and white-screen the panel.
      if (!Array.isArray(domains)) {
        throw new Error('PRO 트리 데이터를 불러올 수 없습니다.');
      }
      return toProTree({ domains });
    } catch (e) {
      rethrow(e);
    }
  },
};
